/**
 * Your own account.
 *
 * IT SHOWS THE ROLE, PROMINENTLY, because every other screen in this panel is
 * shaped by it and none of them say so. A viewer who cannot find the Save button
 * on the agent form, or the Start a call door, is owed one page that explains
 * why in a sentence rather than leaving them to conclude the app is broken.
 *
 * ONE FORM, AND IT IS THE PASSWORD. Email is not editable here — it is the login
 * identity and changing it is an admin action with an ownership proof behind it,
 * which the contract has no route for. An input that looked editable and had
 * nowhere to submit to would be worse than the plain text it replaced.
 *
 * THE CURRENT PASSWORD IS ASKED FOR AND `bad_credentials` SAYS SO SPECIFICALLY.
 * The endpoint takes both because a live session is not proof of who is at the
 * keyboard — an unlocked laptop is the whole threat this field exists for — and a
 * wrong one is the single most likely failure on this form. Reporting it as
 * anything vaguer sends people to change a new password that was never rejected.
 *
 * NO CLAIM ABOUT OTHER SESSIONS. Whether the panel signs your other browsers out
 * when the password changes is the panel's business and the contract does not
 * say, so this screen does not tell anybody either way. The Users screen has an
 * explicit button for it, which is the honest place for that promise.
 */
import { useState } from 'react'
import { ApiError } from '@/api'
import { auth, type Role } from '@/lib/auth'
import { useAuth } from '@/components/auth-provider'
import { Link } from '@/components/app-sidebar'
import { PageHeader } from '@/components/data-table'
import { PasswordField, passwordPairProblem } from '@/components/password-field'
import { Button } from '@/components/ui/button'
import { fmtWhen } from '@/components/state-pill'

/** Keyed by Role rather than by string, so adding a role fails to compile here
 *  instead of silently rendering nothing under somebody's role name. */
const ROLE_MEANS: Record<Role, string> = {
  admin: 'You can read and change everything, and you manage who else has access.',
  power: 'You can read and change everything except who has access.',
  viewer: 'You can read every screen. You cannot change anything or start a call, so the controls that would do either are not shown to you.',
}

type Save =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'error'; message: string }

export function AccountPage() {
  const { user, signOut } = useAuth()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [save, setSave] = useState<Save>({ kind: 'idle' })

  // The shell only renders this screen when there is a session, so this is a
  // type narrowing rather than a case anybody reaches.
  if (!user) return <p className="loading">No session.</p>

  const problem = passwordPairProblem(next, confirm)
  const ready = current !== '' && problem === null

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!ready || save.kind === 'saving') return
    setSave({ kind: 'saving' })
    // A write, so a timeout signal rather than a controller tied to this
    // component. Same rule as Save on the agent form.
    auth
      .changePassword(current, next, AbortSignal.timeout(20_000))
      .then(() => {
        // Cleared on success, all three. A password left sitting in a form after
        // it has been used is a password in a screenshot.
        setCurrent('')
        setNext('')
        setConfirm('')
        setSave({ kind: 'saved' })
      })
      .catch((err: unknown) => {
        setSave({
          kind: 'error',
          message:
            err instanceof ApiError && err.code === 'bad_credentials'
              ? 'Your current password is wrong. The new one was not the problem and has not been used.'
              // weak_password lands here, and its message names the rule broken.
              : err instanceof Error
                ? err.message
                : String(err),
        })
      })
  }

  return (
    <div className="min-w-0 max-w-2xl">
      <PageHeader
        title="Your account"
        subtitle="Who you are signed in as, and the one thing about it you can change from here."
      />

      <section className="panel">
        <h2>
          Identity
          <span className="note">set by an administrator</span>
        </h2>
        <dl className="body grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
          <dt className="text-muted-foreground">Email</dt>
          <dd className="mono min-w-0 break-all">{user.email}</dd>
          <dt className="text-muted-foreground">Name</dt>
          <dd className="min-w-0">
            {user.name ?? <span className="empty">not set</span>}
          </dd>
          <dt className="text-muted-foreground">Role</dt>
          <dd className="min-w-0">
            <span className="mono">{user.role}</span>
            <span className="block text-xs text-muted-foreground">
              {ROLE_MEANS[user.role]}
            </span>
          </dd>
          <dt className="text-muted-foreground">Last login</dt>
          <dd className="min-w-0">{fmtWhen(user.lastLoginAt)}</dd>
          <dt className="text-muted-foreground">Account created</dt>
          <dd className="min-w-0">{fmtWhen(user.createdAt)}</dd>
        </dl>
      </section>

      <section className="panel">
        <h2>Change your password</h2>
        <form onSubmit={onSubmit} className="body flex max-w-md flex-col gap-4">
          <PasswordField
            id="current"
            label="Current password"
            value={current}
            onChange={setCurrent}
            autoComplete="current-password"
            hint="Asked for because a signed-in browser is not proof of who is sitting at it."
          />
          <PasswordField
            id="next"
            label="New password"
            value={next}
            onChange={setNext}
            autoComplete="new-password"
            showRule
          />
          <PasswordField
            id="confirmNew"
            label="New password again"
            value={confirm}
            onChange={setConfirm}
            autoComplete="new-password"
          />

          {save.kind === 'error' && <p className="err text-sm">{save.message}</p>}

          {save.kind !== 'error' && problem && next !== '' && confirm !== '' && (
            <p className="text-sm text-muted-foreground">{problem}</p>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <Button type="submit" disabled={!ready || save.kind === 'saving'}>
              {save.kind === 'saving' ? 'Changing…' : 'Change password'}
            </Button>
            {save.kind === 'saved' && (
              <span className="text-sm text-ev-good">
                Changed. Use the new password next time you sign in.
              </span>
            )}
          </div>
        </form>
      </section>

      <p className="text-sm text-muted-foreground">
        Finished?{' '}
        <button
          type="button"
          onClick={signOut}
          className="underline underline-offset-4 hover:text-foreground"
        >
          Sign out
        </button>
        . Your role is set by an administrator on the{' '}
        <Link to="/users" className="underline underline-offset-4">
          Users
        </Link>{' '}
        screen.
      </p>
    </div>
  )
}
